import React from 'react'
import getClass from '../classNames'
import PropTypes from 'prop-types'
import createProps from '../createProps'

import {
  AlignContentType,
  AlignItemsType,
  AlignSelfType,
  ColsType,
  ContainerSizeType,
  DisplayType,
  FlexDirectionType,
  JustifyContentType,
  SIZES,
  TextAlignType,
  VAlignType,
  WrapType,
} from '../types'

import {
  isArray,
  isBoolean,
  isNumeric,
  isObject,
  isString,
} from '../../../utils'

export const propTypes = {
  ...SIZES.reduce(
    (acc, size) => ({
      ...acc,
      [size]: PropTypes.oneOfType([ContainerSizeType, PropTypes.object]),
    }),
    {},
  ),
  alignContent: AlignContentType,
  alignItems: AlignItemsType,
  alignSelf: AlignSelfType,
  justifyContent: JustifyContentType,
  className: PropTypes.string,
  tagName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  component: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.object,
  ]),
  children: PropTypes.node,
  flexDirection: FlexDirectionType,
  direction: FlexDirectionType,
  wrap: WrapType,
  flexWrap: WrapType,
  flexAlign: AlignItemsType,
  flexJustify: JustifyContentType,
  textAlign: TextAlignType,
  vAlign: VAlignType,
  display: DisplayType,
  cols: ColsType,
  fluid: PropTypes.bool,
  centered: PropTypes.bool,
  gap: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.number,
  ]),
}

const classMap = {
  ...SIZES.reduce(
    (acc, size) => ({
      ...acc,
      [size]: `container-cols${size !== 's' ? '-' + size : ''}`,
    }),
    {},
  ),
  cols: 'container-cols',
  alignContent: 'align-content',

  alignItems: 'align-items',
  justifyContent: 'justify-content',
  alignSelf: 'align-self',
  flexDirection: 'flex-direction',
  flexAlign: 'align-items',
  flexJustify: 'justify-content',
  vAlign: 'vertical-align',
  wrap: 'flex-wrap',
  direction: 'flex-direction',
  fluid: 'container',
  centered: 'container',
}

const varsMap = {
  gap: 'gap',
  ...SIZES.reduce(
    (acc, size) => ({
      ...acc,
      [size]: `gap${size !== 's' ? '-' + size : ''}`,
    }),
    {},
  ),
}

const valueMap = {
  'flex-start': 'start',
  'flex-end': 'end',
  'space-between': 'between',
  'space-around': 'around',
  'space-evenly': 'evenly',
}

const booleanMap = {
  wrap: {
    true: 'wrap',
    false: 'nowrap',
  },
  fluid: {
    true: 'fluid',
    false: 'unfluid',
  },
  centered: {
    true: 'centered',
    false: 'uncentered',
  },
}

function getContainerClassNames({ className, ...rest }) {
  const extraClasses = [className, getClass('container')]

  const classes = Object.keys(rest)
    .filter((key) => classMap[key])
    .reduce((acc, key) => {
      if (typeof rest[key] === 'object' && rest[key] !== null) {
        Object.entries(rest[key])
          .filter(([k]) => classMap[k])
          .map(([k, v]) =>
            getClass(
              isNumeric(v) || isString(v)
                ? classMap[k] + '-' + key + '-' + (valueMap?.[v] ?? v)
                : isBoolean(v)
                ? classMap[k] + '-' + key + '-' + (booleanMap?.[k]?.[v] ?? v)
                : classMap[k],
            ),
          )
          .forEach((item) => acc.push(item))
      } else {
        acc.push(
          getClass(
            isNumeric(rest[key]) || isString(rest[key])
              ? classMap[key] + '-' + (valueMap?.[rest[key]] ?? rest[key])
              : isBoolean(rest[key])
              ? classMap[key] +
                '-' +
                (booleanMap?.[key]?.[rest[key]] ?? rest[key])
              : classMap[key],
          ),
        )
      }
      return acc
    }, [])
  return classes.concat(extraClasses)
}

function getVar({ prop, mod = null, value }) {
  switch (prop) {
    case 'gap': {
      let [rowGap = 0, columnGap = rowGap] = isString(value)
        ? value.split(' ')
        : isNumeric(value)
        ? [value]
        : isArray(value)
        ? value
        : [0]
      return [
        `--row-gap${mod ? '-' + mod : ''}: ${rowGap}${
          isNumeric(rowGap) ? 'px' : ''
        }`,
        `--column-gap${mod ? '-' + mod : ''}: ${columnGap}${
          isNumeric(columnGap) ? 'px' : ''
        }`,
      ]
    }
    default:
      return [`--${varsMap[prop]}${mod ? '-' + mod : ''}: ${value}`]
  }
}

function getContainerVars(props) {
  const vars = Object.keys(props)
    .filter((key) => varsMap[key])
    .reduce((acc, key) => {
      const prop = props[key]
      if (isObject(prop) && !isArray(prop)) {
        Object.entries(prop)
          .filter(([k]) => !!varsMap[k])
          .forEach(([k, v]) => {
            acc.push(...getVar({ prop: varsMap[k], mod: key, value: v }))
          })
      } else {
        acc.push(...getVar({ prop: varsMap[key], value: prop }))
      }
      return acc
    }, [])
  return vars
}

export function getContainerProps({ ...rest }) {
  return createProps(
    propTypes,
    rest,
    getContainerClassNames(rest),
    getContainerVars(rest),
  )
}

Container.propTypes = propTypes

export default function Container({
  tagName = null,
  as = null,
  component = null,
  ...containerProps
}) {
  return React.createElement(
    tagName || as || component || 'div',
    getContainerProps(containerProps),
  )
}

Container.defaultProps = {
  wrap: 'wrap',
  fluid: true,
  centered: false,
}
